import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useMkRoot } from "./branding";

type ToastType = "success" | "info" | "warning" | "danger" | "primary";

interface ToastAction {
    label: string;
    close?: boolean;
    onClick?(): void;
}

interface ToastOptions {
    title?: string;
    icon?: string;
    sticky?: boolean;
    actions?: ToastAction[];
    duration?: number;
}

interface Toast {
    id: number;
    type: ToastType;
    message: string;
    title: string;
    icon: string;
    sticky: boolean;
    actions: ToastAction[];
}

interface ToastApi {
    push(text: string, tone?: ToastType): void;
    add(message: string, options?: ToastOptions & { type?: ToastType }): number;
    remove(id: number): void;
    success(message: string, options?: ToastOptions): number;
    info(message: string, options?: ToastOptions): number;
    warning(message: string, options?: ToastOptions): number;
    danger(message: string, options?: ToastOptions): number;
}

const DEFAULT_DURATION = 4200;

const ICONS: Record<ToastType, string> = {
    success: "fa-check-circle",
    info: "fa-info-circle",
    warning: "fa-exclamation-triangle",
    danger: "fa-times-circle",
    primary: "fa-bell",
};

const ToastContext = createContext<ToastApi | null>(null);

let sequence = 0;

function ToastStack({ toasts, onDismiss }: {
    toasts: Toast[];
    onDismiss(id: number): void;
}) {
    const { t } = useTranslation();
    const root = useMkRoot();
    return (
        <div className="mk mk-toast-stack" {...root}>
            {toasts.map((toast) => (
                <div key={toast.id} className={`mk-toast mk-toast-${toast.type}`} role="status">
                    <span className="mk-toast-icon">
                        <i className={`fa ${toast.icon}`} />
                    </span>
                    <div className="mk-toast-body">
                        {toast.title && <div className="mk-toast-title">{toast.title}</div>}
                        <div className="mk-toast-message">{toast.message}</div>
                        {toast.actions.length > 0 && (
                            <div className="mk-toast-actions">
                                {toast.actions.map((action, index) => (
                                    <button key={index} className="mk-toast-action"
                                            onClick={() => {
                                                action.onClick?.();
                                                if (action.close !== false) {
                                                    onDismiss(toast.id);
                                                }
                                            }}>
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button className="mk-toast-close" onClick={() => onDismiss(toast.id)}
                            aria-label={t("mk.dismiss")}>
                        <i className="fa fa-times" />
                    </button>
                    {!toast.sticky && <span className="mk-toast-progress" />}
                </div>
            ))}
        </div>
    );
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const timers = useRef(new Map<number, number>());

    const remove = useCallback((id: number) => {
        const timer = timers.current.get(id);
        if (timer) {
            window.clearTimeout(timer);
            timers.current.delete(id);
        }
        setToasts((current) => current.filter((toast) => toast.id !== id));
    }, []);

    const add = useCallback((message: string, options: ToastOptions & { type?: ToastType } = {}) => {
        const id = ++sequence;
        const type: ToastType = options.type && ICONS[options.type] ? options.type : "primary";
        const toast: Toast = {
            id,
            type,
            message,
            title: options.title || "",
            icon: options.icon || ICONS[type],
            sticky: Boolean(options.sticky),
            actions: options.actions || [],
        };
        setToasts((current) => [...current, toast]);
        if (!toast.sticky) {
            timers.current.set(id, window.setTimeout(() => remove(id),
                                                     options.duration || DEFAULT_DURATION));
        }
        return id;
    }, [remove]);

    const value = useMemo<ToastApi>(() => ({
        add,
        remove,
        push: (text, tone = "info") => {
            add(text, { type: tone });
        },
        success: (message, options) => add(message, { ...options, type: "success" }),
        info: (message, options) => add(message, { ...options, type: "info" }),
        warning: (message, options) => add(message, { ...options, type: "warning" }),
        danger: (message, options) => add(message, { ...options, type: "danger" }),
    }), [add, remove]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastStack toasts={toasts} onDismiss={remove} />
        </ToastContext.Provider>
    );
}

export function useToast(): ToastApi {
    const value = useContext(ToastContext);
    if (!value) {
        throw new Error("useToast must be used inside ToastProvider");
    }
    return value;
}
